import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef, MatFormFieldAppearance, MatTabChangeEvent, MatTabGroup, ThemePalette} from '@angular/material';
import {AngularFireAuth} from '@angular/fire/auth';
import {isPlatformBrowser} from '@angular/common';
import {Subscription} from 'rxjs/internal/Subscription';

import {AuthProcessService, AuthProvider} from '../../services/auth-process.service';
import {LegalityDialogComponent} from '../../components/legality-dialog/legality-dialog.component';
import {LegalityDialogParams, LegalityDialogResult} from '../../interfaces/legality.dialog.intreface';
import {NgxAuthFirebaseUIConfig, NgxAuthFirebaseUIConfigToken} from '../../ngx-auth-firebase-u-i.module';
import {defaultAuthFirebaseUIConfig} from '../../interfaces/config.interface';
import {MatPasswordStrengthComponent} from '@angular-material-extensions/password-strength';


export const EMAIL_REGEX = new RegExp(['^(([^<>()[\\]\\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\\.,;:\\s@\"]+)*)',
  '|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.',
  '[0-9]{1,3}\])|(([a-zA-Z\\-0-9]+\\.)+',
  '[a-zA-Z]{2,}))$'].join(''));

export const PHONE_NUMBER_REGEX = new RegExp(/^\+(?:[0-9] ?){6,14}[0-9]$/);

@Component({
  selector: 'ngx-auth-firebaseui',
  template: `
    <mat-tab-group [color]="color" [selectedIndex]="tabIndex" (selectedTabChange)="onTabChange($event)">

      <!--Sign in tab-->
      <mat-tab [label]="signInTabText">
        <mat-card>
          <mat-card-title>{{signInCardTitleText}}</mat-card-title>
          <mat-card-content>
            <form [formGroup]="signInFormGroup"
                  (ngSubmit)="signInFormGroup.valid &&
                  authProcess.signInWith
                  (authProviders.EmailAndPassword,
                  {email:signInFormGroup.value.email, password: signInFormGroup.value.password})">
              <div fxLayout="column" fxLayoutAlign="center">
                <mat-form-field [appearance]="appearance">
                  <mat-label>{{emailText}}</mat-label>
                  <input matInput
                         formControlName="email"
                         required>
                  <mat-icon matSuffix [color]="color">email</mat-icon>
                  <mat-error *ngIf="signInEmailFormControl.hasError('required')">
                    {{emailErrorRequiredText}}
                  </mat-error>
                  <mat-error *ngIf="signInEmailFormControl.hasError('pattern')">
                    {{emailErrorPatternText}}
                  </mat-error>
                </mat-form-field>

                <mat-form-field [appearance]="appearance">
                  <mat-label>{{passwordText}}</mat-label>
                  <input matInput
                         type="password"
                         minlength="6"
                         maxlength="25"
                         formControlName="password"
                         required>
                  <mat-icon matSuffix [color]="color">lock</mat-icon>
                  <mat-hint align="end" aria-live="polite">
                    {{signInFormGroup.value.password.length}} / 25
                  </mat-hint>
                  <mat-error *ngIf="sigInPasswordFormControl.hasError('required')">
                    {{passwordErrorRequiredText}}
                  </mat-error>
                  <mat-error *ngIf="sigInPasswordFormControl.hasError('minlength')">
                    The password must be at least 6 characters long.
                  </mat-error>
                  <mat-error *ngIf="sigInPasswordFormControl.hasError('maxlength')">
                    The password must not exceed 25 characters
                  </mat-error>
                </mat-form-field>

                <button mat-raised-button
                        style="margin-top: 20px"
                        type="submit"
                        class="space-top"
                        [color]="color">
                  {{loginButtonText}}
                </button>

              </div>
            </form>

            <div fxLayoutAlign="center">
              <button *ngIf="resetPasswordEnabled"
                      mat-button
                      class="space-top"
                      [color]="color"
                      (click)="createForgotPasswordTab()">
                {{forgotPasswordButtonText}}
              </button>
            </div>

          </mat-card-content>
          <mat-card-footer *ngIf="authProcess.isLoading">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          </mat-card-footer>
        </mat-card>
      </mat-tab>

      <!--tab register-->
      <mat-tab [label]="registerTabText" *ngIf="registrationEnabled">
        <mat-card>
          <mat-card-title>{{registerCardTitleText}}</mat-card-title>
          <div *ngIf="!authProcess.emailConfirmationSent;then register else confirm"></div>
          <ng-template #register>
            <mat-card-content fxLayout="column" fxLayoutAlign="center">
              <form [formGroup]="signUpFormGroup" (ngSubmit)="signUpFormGroup.valid &&
              processLegalSignUP(authProvider.EmailAndPassword)">
                <div fxLayout="column" fxLayoutAlign="center">
                  <!--name-->
                  <mat-form-field [appearance]="appearance">
                    <!--labels will work only with @angular/material@6.2.0 -->
                    <mat-label>{{nameText}}</mat-label>
                    <input matInput
                           [minlength]="2"
                           [maxlength]="20"
                           [formControl]="sigUpNameFormControl"
                           required>
                    <mat-icon matSuffix [color]="color">person</mat-icon>
                    <mat-hint align="end" aria-live="polite">
                      {{signUpFormGroup.value.name?.length}} / {{passwordStrength.max}}
                    </mat-hint>
                    <mat-error *ngIf="sigUpNameFormControl.hasError('required')">
                      {{nameErrorRequiredText}}
                    </mat-error>
                    <mat-error *ngIf="sigUpNameFormControl.hasError('minlength')">
                      {{nameErrorMinLengthText}}
                    </mat-error>
                    <mat-error *ngIf="sigUpNameFormControl.hasError('maxlength')">
                      {{nameErrorMaxLengthText}}
                    </mat-error>
                  </mat-form-field>

                  <!--email-->
                  <mat-form-field [appearance]="appearance">
                    <mat-label>{{emailText}}</mat-label>
                    <input matInput
                           type="email"
                           [formControl]="sigUpEmailFormControl"
                           required>
                    <mat-icon matSuffix [color]="color">email</mat-icon>
                    <mat-error *ngIf="sigUpEmailFormControl.hasError('required')">
                      {{emailErrorRequiredText}}
                    </mat-error>
                    <mat-error *ngIf="sigUpEmailFormControl.hasError('pattern')">
                      {{emailErrorPatternText}}
                    </mat-error>
                  </mat-form-field>

                  <!--password-->
                  <div fxLayout="column">
                    <mat-form-field [appearance]="appearance">
                      <mat-label>{{passwordText}}</mat-label>
                      <input matInput
                             [type]="toggle.type"
                             name="password"
                             [formControl]="sigUpPasswordFormControl"
                             required>
                      <mat-pass-toggle-visibility #toggle matSuffix></mat-pass-toggle-visibility>

                      <mat-icon matSuffix [color]="color">lock</mat-icon>

                      <mat-hint align="end" aria-live="polite">
                        {{signUpFormGroup.value.password?.length}} / {{ max }}
                      </mat-hint>

                      <mat-error *ngIf="sigUpPasswordFormControl.hasError('required')" class="cut-text">
                        {{passwordErrorRequiredText}}
                      </mat-error>

                      <mat-error *ngIf="sigUpPasswordFormControl.hasError('minlength')" class="cut-text">
                        The password must be at least {{min}} characters long.
                      </mat-error>
                      <mat-error *ngIf="sigUpPasswordFormControl.hasError('maxlength')" class="cut-text">
                        The password can not be longer than {{max}} characters.
                      </mat-error>

                    </mat-form-field>

                    <mat-password-strength #passwordStrength
                                           [min]="min"
                                           [max]="max"
                                           [customValidator]="customValidator"
                                           [enableLengthRule]="enableLengthRule"
                                           [enableLowerCaseLetterRule]="enableLowerCaseLetterRule"
                                           [enableUpperCaseLetterRule]="enableUpperCaseLetterRule"
                                           [enableDigitRule]="enableDigitRule"
                                           [enableSpecialCharRule]="enableSpecialCharRule"
                                           [password]="signUpFormGroup.value.password"
                                           [externalError]="sigUpPasswordFormControl.dirty">
                    </mat-password-strength>

                  </div>

                  <button mat-raised-button
                          style="margin-top: 20px"
                          type="submit"
                          [color]="color">
                    {{registerButtonText}}
                  </button>

                </div>
              </form>

              <button *ngIf="guestEnabled"
                      mat-button
                      style="margin-top: 20px"
                      [color]="color"
                      (click)="processLegalSignUP(authProvider.ANONYMOUS)">
                <mat-icon>fingerprint</mat-icon>
                {{guestButtonText}}
              </button>

            </mat-card-content>

            <mat-card-footer *ngIf="authProcess.isLoading">
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            </mat-card-footer>

          </ng-template>

          <!--confirm template-->
          <ng-template #confirm>
            <ngx-auth-firebaseui-email-confirmation [email]="authProcess.emailToConfirm"
                                                    [goBackURL]="goBackURL">
            </ngx-auth-firebaseui-email-confirmation>
          </ng-template>

        </mat-card>
      </mat-tab>

      <!--Reset password tab-->
      <mat-tab *ngIf="passwordResetWished" [label]="resetPasswordTabText">
        <form [formGroup]="resetPasswordFormGroup" (ngSubmit)="resetPasswordFormGroup.valid && resetPassword()">

          <mat-card>
            <mat-card-header fxLayoutAlign="end">
              <button mat-icon-button
                      [color]="color"
                      matTooltip="close"
                      matTooltipPosition="above"
                      (click)="passwordResetWished = false">
                <mat-icon>close</mat-icon>
              </button>
            </mat-card-header>

            <mat-card-content>
              <mat-form-field class="full-width" [appearance]="appearance">
                <mat-label> {{ resetPasswordTabText }} </mat-label>
                <input matInput
                       [readonly]="passReset"
                       [title]="resetPasswordInputText"
                       formControlName="email"
                       required>
                <mat-icon matSuffix [color]="color">email</mat-icon>
                <mat-error *ngIf="resetPasswordEmailFormControl.hasError('required')">
                  {{resetPasswordErrorRequiredText}}
                </mat-error>
                <mat-error *ngIf="resetPasswordEmailFormControl.hasError('pattern')">
                  {{resetPasswordErrorPatternText}}
                </mat-error>
              </mat-form-field>
            </mat-card-content>
            <mat-card-actions fxLayoutAlign="center">
              <button mat-raised-button
                      type="submit"
                      [color]="color"
                      [disabled]="passReset">
                {{resetPasswordActionButtonText}}
              </button>
            </mat-card-actions>
            <mat-card-footer *ngIf="passReset" fxLayoutAlign="center">
              <p>{{resetPasswordInstructionsText}}</p>
            </mat-card-footer>
            <mat-card-footer>
              <mat-progress-bar *ngIf="authProcess.isLoading" mode="indeterminate"></mat-progress-bar>
            </mat-card-footer>
          </mat-card>
        </form>
      </mat-tab>

    </mat-tab-group>
    <mat-divider></mat-divider>
    <ngx-auth-firebaseui-providers [providers]="providers"></ngx-auth-firebaseui-providers>
  `,
  styles: [`
    .mat-card{margin:2rem}.space-top{margin-top:.5rem}.full-width{width:100%}.cut-text{text-overflow:ellipsis;overflow:hidden;white-space:nowrap}
  `]
})
export class AuthComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @ViewChild(MatTabGroup, {static: false}) matTabGroup: MatTabGroup;
  @ViewChild(MatPasswordStrengthComponent, {static: false}) passwordStrength: MatPasswordStrengthComponent;

  @Input() providers: string[] | string = AuthProvider.ALL; //  google, facebook, twitter, github as array or all as one single string
  @Input() appearance: MatFormFieldAppearance;
  @Input() tabIndex: number | null;
  @Input() registrationEnabled = true;
  @Input() resetPasswordEnabled = true;
  @Input() guestEnabled = true;
  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;
  @Input() goBackURL: string;
  @Input() messageOnAuthSuccess: string;
  @Input() messageOnAuthError: string;

  @Output() onSuccess: any;
  @Output() onError: any;
  @Output() selectedTabChange: EventEmitter<MatTabChangeEvent> = new EventEmitter();

  // Password strength api
  @Input() enableLengthRule = true;
  @Input() enableLowerCaseLetterRule = true;
  @Input() enableUpperCaseLetterRule = true;
  @Input() enableDigitRule = true;
  @Input() enableSpecialCharRule = true;
  @Input() min = 8;
  @Input() max = 30;
  @Input() customValidator: RegExp;
  @Output() onStrengthChanged: EventEmitter<number> = new EventEmitter();

  // Customize the text
  // Reset Password Tab
  @Input() resetPasswordTabText = 'Reset e-mail address to password';
  @Input() resetPasswordInputText = 'Reset e-mail address to password';
  @Input() resetPasswordErrorRequiredText = 'E-mail is required to reset the password!';
  @Input() resetPasswordErrorPatternText = 'Please enter a valid e-mail address';
  @Input() resetPasswordActionButtonText = 'Reset';
  @Input() resetPasswordInstructionsText = 'Reset requested. Check your e-mail instructions.';

  // SignIn Tab
  @Input() signInTabText = 'Sign in';
  @Input() signInCardTitleText = 'Signing in';
  @Input() loginButtonText = 'Log In';
  @Input() forgotPasswordButtonText = 'Forgot Password ?';

  // Common
  @Input() nameText = 'Name';
  @Input() nameErrorRequiredText = 'Name is required';
  @Input() nameErrorMinLengthText = 'The name is too short!';
  @Input() nameErrorMaxLengthText = 'The name is too long!';

  @Input() emailText = 'E-mail';
  @Input() emailErrorRequiredText = 'E-mail is required';
  @Input() emailErrorPatternText = 'Please enter a valid e-mail address';

  @Input() passwordText = 'Password';
  @Input() passwordErrorRequiredText = 'Password is required';

  // Register Tab
  @Input() registerTabText = 'Register';
  @Input() registerCardTitleText = 'Registration';
  @Input() registerButtonText = 'Register';
  @Input() guestButtonText = 'continue as guest';

  authProvider = AuthProvider;
  passwordResetWished: boolean;

  public signInFormGroup: FormGroup;
  public signUpFormGroup: FormGroup;
  public resetPasswordFormGroup: FormGroup;

  onErrorSubscription: Subscription;
  authenticationError = false;

  passReset = false;
  dialogRef: MatDialogRef<LegalityDialogComponent>;

  authProviders = AuthProvider;

  signInEmailFormControl: AbstractControl;
  sigInPasswordFormControl: AbstractControl;

  sigUpNameFormControl: AbstractControl;
  sigUpEmailFormControl: AbstractControl;
  sigUpPasswordFormControl: AbstractControl;
  sigUpPasswordConfirmationFormControl: AbstractControl;
  resetPasswordEmailFormControl: AbstractControl;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              @Inject(NgxAuthFirebaseUIConfigToken) private config: NgxAuthFirebaseUIConfig,
              public auth: AngularFireAuth,
              public authProcess: AuthProcessService,
              public dialog: MatDialog) {

    this.onSuccess = authProcess.onSuccessEmitter;
    this.onError = authProcess.onErrorEmitter;
  }

  public ngOnInit(): void {
    this.config = Object.assign(defaultAuthFirebaseUIConfig, this.config);

    if (isPlatformBrowser(this.platformId)) {
      this.onErrorSubscription = this.onError.subscribe(() => this.authenticationError = true);
    }
    this.updateAuthSnackbarMessages();
    // auth form's initialization
    this._initSignInFormGroupBuilder();
    this._initSignUpFormGroupBuilder();
    this._initResetPasswordFormGroupBuilder();
  }

  ngAfterViewInit(): void {
    if (this.passwordStrength) {
      this.passwordStrength.onStrengthChanged.subscribe((strength: number) => {
        this.onStrengthChanged.emit(strength);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messageOnAuthSuccess || changes.messageOnAuthError) {
      this.updateAuthSnackbarMessages();
    }
  }

  ngOnDestroy(): void {
    if (this.onErrorSubscription) {
      this.onErrorSubscription.unsubscribe();
    }
  }

  onTabChange(event: MatTabChangeEvent) {
    this.selectedTabChange.emit(event);
  }

  get color(): string | ThemePalette {
    return this.authenticationError ? 'warn' : 'primary';
  }

  public updateAuthSnackbarMessages(): void {
    this.authProcess.messageOnAuthSuccess = this.messageOnAuthSuccess;
    this.authProcess.messageOnAuthError = this.messageOnAuthError;
  }

  public createForgotPasswordTab() {
    this.passwordResetWished = true;
    this.tabIndex = 2;
  }

  public processLegalSignUP(authProvider?: AuthProvider) {
    if (this.tosUrl || this.privacyPolicyUrl) {
      const params: LegalityDialogParams = {
        tosUrl: this.tosUrl,
        privacyPolicyUrl: this.privacyPolicyUrl,
        authProvider: authProvider
      };

      this.dialogRef = this.dialog.open(LegalityDialogComponent, {data: params});
      this.dialogRef.afterClosed().subscribe((result: LegalityDialogResult) => {
        // console.log('this.dialogRef.afterClosed(): ', result);
        if (result && result.checked) {
          this._afterSignUpMiddleware(result.authProvider).then(() => this.signUpFormGroup.reset());
        }
        this.dialogRef = null;
      });
    } else {
      this._afterSignUpMiddleware(authProvider).then(() => this.signUpFormGroup.reset());
    }
  }

  public async signUp() {
    return await this.authProcess.signUp
    (
      this.signUpFormGroup.value.name,
      {
        email: this.signUpFormGroup.value.email,
        password: this.signUpFormGroup.value.password
      }
    );
  }

  public async signUpAnonymously() {
    return await this.authProcess.signInWith(this.authProvider.ANONYMOUS);
  }


  public resetPassword() {
    this.authProcess.resetPassword(this.resetPasswordEmailFormControl.value)
      .then(() => {
        this.passReset = true;
        setTimeout(() => this.tabIndex = 2, 10);
      });
  }

  private _initSignInFormGroupBuilder() {
    this.signInFormGroup = new FormGroup({});
    this.signInFormGroup.registerControl('email', this.signInEmailFormControl = new FormControl('',
      [
        Validators.required,
        Validators.pattern(EMAIL_REGEX)
      ]));
    this.signInFormGroup.registerControl('password', this.sigInPasswordFormControl = new FormControl('',
      [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(25),
      ]));
  }

  private _initSignUpFormGroupBuilder() {
    this.signUpFormGroup = new FormGroup({
      name: this.sigUpNameFormControl = new FormControl('',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(30),
        ]),
      email: this.sigUpEmailFormControl = new FormControl('',
        [
          Validators.required,
          Validators.pattern(EMAIL_REGEX)
        ]),
      password: this.sigUpPasswordFormControl = new FormControl('',
        [
          Validators.required,
          Validators.minLength(this.min),
          Validators.maxLength(this.max),
        ])
    });
  }

  private _initResetPasswordFormGroupBuilder() {
    this.resetPasswordFormGroup = new FormGroup({
      email: this.resetPasswordEmailFormControl = new FormControl('',
        [
          Validators.required,
          Validators.pattern(EMAIL_REGEX)
        ])
    });
  }

  private _afterSignUpMiddleware(authProvider?: AuthProvider) {
    if (authProvider === this.authProvider.ANONYMOUS) {
      return this.signUpAnonymously();
    }
    return this.signUp();
  }

}
